import type { GqlClientService } from '@/common/network/gql-client-service.ts'
import type { ObjectTypesService } from '@/object-types/services/ObjectTypesService.ts'
import * as op from '@/common/errors/OperationResult.ts'
import { OperationResult } from '@/common/errors/OperationResult.ts'
import type {
  CollectionTabModel as GqlCollectionTabModel,
  CollectionViewSettingsModel,
  DeleteCollectionTabInput,
  FilterNodeModel as GqlFilterNodeModel,
  UpdateCollectionTabInput,
  UpdateCollectionViewSettingsInput,
} from '@/generated/graphql.ts'
import type { CollectionTabModel, CreateCollectionTabInput } from '@/core/gql/gql-models-override.ts'
import type { FilterNodeModel } from '@/core/gql/filter-models.ts'

type GqlViewSettingsResponse = {
  collectionViewSettings: CollectionViewSettingsModel
  collectionTabs: GqlCollectionTabModel[]
}

type ParsedViewSettingsResponse = {
  collectionViewSettings: CollectionViewSettingsModel
  collectionTabs: CollectionTabModel[]
}

type UpdateViewSettingsResponse = {
  updateCollectionViewSettings: CollectionViewSettingsModel
}

type UpdateCollectionTabResponse = {
  updateCollectionTab: GqlCollectionTabModel
}

type CreateCollectionTabResponse = {
  createCollectionTab: GqlCollectionTabModel
}

type DeleteCollectionTabResponse = {
  deleteCollectionTab: boolean
}

const tabGqlFields = `
      id
      collectionId
      name
      filterTree {
        id
        parentId
        logicalOperator
        value
        propertyDefinitionId
        operator
        values
      }`

const viewSettingsGqlFields = `
      id
      collectionId
      groupByColumnId
      columns {
        propertyDefinitionId
        order
        widthWeight
        isVisible
      }
`

export class CollectionViewsApiService {
  constructor(
    private readonly gqlClientService: GqlClientService,
    private readonly typesService: ObjectTypesService,
  ) {}

  public async updateViewSettings(
    input: UpdateCollectionViewSettingsInput,
  ): Promise<OperationResult<CollectionViewSettingsModel>> {
    const query = `mutation($input: UpdateCollectionViewSettingsInput!) {
    updateCollectionViewSettings(input: $input) {${viewSettingsGqlFields}}
  }`

    const { result: body, error } =
      await this.gqlClientService.sendGqlRequest<UpdateViewSettingsResponse>(query, {
        variables: { input },
      })

    if (error) {
      return op.error(error)
    }
    return op.success(body!.data!.updateCollectionViewSettings)
  }

  public async createCollectionTab(
    input: CreateCollectionTabInput,
  ): Promise<OperationResult<CollectionTabModel>> {
    const query = `mutation($input: CreateCollectionTabInput!) {
    createCollectionTab(input: $input) {${tabGqlFields}}
  }`

    const { result: body, error } =
      await this.gqlClientService.sendGqlRequest<CreateCollectionTabResponse>(query, {
        variables: { input },
      })

    if (error) {
      return op.error(error)
    }
    const tab = body!.data!.createCollectionTab
    return op.success({
      ...tab,
      filterTree: tab.filterTree.map(this.parseFilterNode.bind(this)),
    })
  }

  public async deleteCollectionTab(
    input: DeleteCollectionTabInput,
  ): Promise<OperationResult<boolean>> {
    const query = `mutation($input: DeleteCollectionTabInput!) {
    deleteCollectionTab(input: $input)
  }`

    const { result: body, error } =
      await this.gqlClientService.sendGqlRequest<DeleteCollectionTabResponse>(query, {
        variables: { input },
      })

    if (error) {
      return op.error(error)
    }
    return op.success(body!.data!.deleteCollectionTab)
  }

  public async updateCollectionTab(
    input: UpdateCollectionTabInput,
  ): Promise<OperationResult<CollectionTabModel>> {
    const query = `mutation($input: UpdateCollectionTabInput!) {
    updateCollectionTab(input: $input) {${tabGqlFields}}
  }`

    const { result: body, error } =
      await this.gqlClientService.sendGqlRequest<UpdateCollectionTabResponse>(query, {
        variables: { input },
      })

    if (error) {
      return op.error(error)
    }
    const tab = body!.data!.updateCollectionTab
    return op.success({
      ...tab,
      filterTree: tab.filterTree.map(this.parseFilterNode.bind(this)),
    })
  }

  public async getViewSettingsAndTabs(
    collectionId: string,
    abortSignal?: AbortSignal,
  ): Promise<OperationResult<ParsedViewSettingsResponse | undefined>> {
    const query = `query($collectionId: ID!) {
    collectionViewSettings(collectionId: $collectionId) {${viewSettingsGqlFields}}
    collectionTabs(collectionId: $collectionId) {${tabGqlFields}}
    }`

    const { result: body, error } =
      await this.gqlClientService.sendGqlRequest<GqlViewSettingsResponse>(query, {
        variables: { collectionId },
        abortSignal,
      })

    if (error) {
      return op.error(error)
    }

    const raw = body!.data!
    if (!raw) return op.success(undefined)

    return op.success({
      collectionViewSettings: raw.collectionViewSettings,
      collectionTabs: raw.collectionTabs.map((tab) => ({
        ...tab,
        filterTree: tab.filterTree.map(this.parseFilterNode.bind(this)),
      })),
    })
  }

  private parseFilterValue(raw: GqlFilterNodeModel): number | string | null {
    if (raw.value == null) return null
    const def = this.typesService.idToPropertyDef.get(raw.propertyDefinitionId!)
    if (def?.type === 'select-1') return Number(raw.value) as number
    return raw.value
  }

  private parseFilterNode(raw: GqlFilterNodeModel): FilterNodeModel {
    return {
      ...raw,
      id: raw.id as number,
      parentId: raw.parentId as number | null,
      value: this.parseFilterValue(raw),
      values: raw.values ? raw.values.map((v) => Number(v) as number) : null,
    } as FilterNodeModel
  }
}
